// La synthèse d'un profil, produite après l'entretien et relue avant d'être écrite.
// Elle remplace la précédente : une synthèse n'est pas un historique, c'est l'état de ce
// qu'on sait. Ce qui a été dit et quand reste dans les notes, qui ne s'effacent pas.
import { api, direRefus } from '../api.ts';
import { $, blocAttente, compteur, h, mot, peindre, valeur, type Brut } from '../ui.ts';
import { ETAPES_CHAINE, NIVEAUX_AXE, NIVEAUX_IA, TERRAINS_IA, niveauAxe,
         type Axe } from '../../domaine/ia.ts';

interface Fait { fait: string; source: string }
interface Vise { appui: string; secteur?: string; direction?: string }

/**
 * La synthèse **entière**, telle que le modèle la rend et telle qu'elle s'enregistre.
 *
 * Les champs au-delà des quatre premiers ne sont pas des détails d'affichage : ce sont
 * eux qui alimentent le positionnement sectoriel et la cartographie IA du dossier. Les
 * déclarer ici est ce qui les fait voyager de la production à l'enregistrement — leur
 * absence a coûté toutes les cartographies produites depuis la mise en service.
 */
export interface Synthese {
  positionnement: string; prouve: Fait[]; vigilance: string[]; ouvertes: string[];
  secteurs_cibles?: Vise[]; directions_cibles?: Vise[];
  ia_niveau?: number; ia_depuis?: number; ia_dernier?: number; ia_justification?: string;
  ia_chaine?: Axe; ia_terrains?: Axe; ia_postures?: string[];
}

interface Lecture {
  profilId: string; fini: () => void; traitement: string; empreinte: string;
  // Ce que le modèle a rendu, gardé entier : le formulaire ne montre que ce qui se
  // corrige au clavier, et tout le reste repart à l'identique au lieu d'être perdu.
  produite: Synthese;
}
let vivante: Lecture | null = null;
let ecoute = false;

const cadre = (titre: string, corps: Brut, pied: Brut): Brut => h`
  <div class="voile" data-act="fermer-voile"><div class="couche large-c">
    <div class="ct"><div><div class="sur">SYNTHÈSE</div><h2>${titre}</h2></div>
      <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
    <div class="cc">${corps}</div>
    <div class="cp">${pied}</div>
  </div></div>`;

const fait = (f: Fait, i: number) => h`
  <div class="rang" style="gap:8px;margin-top:6px;align-items:flex-start">
    <textarea class="champ pousse" data-s="fait" data-i="${i}" rows="2">${f.fait}</textarea>
    <input class="champ" data-s="source" data-i="${i}" value="${f.source}"
      placeholder="d'où cela vient" style="max-width:220px">
  </div>`;

const VIDE: Synthese = { positionnement: '', prouve: [], vigilance: [], ouvertes: [] };

export function ouvrirSynthese(profilId: string, fini: () => void): void {
  vivante = { profilId, fini, traitement: '', empreinte: '', produite: VIDE };
  poserEcoute();
  void produire();
}

async function produire(): Promise<void> {
  peindre('#couche', cadre('Production en cours',
    blocAttente('LE MODÈLE RELIT LE DOSSIER',
      "La fiche, les expériences, les notes de l'équipe et le compte rendu d'entretien s'il est joint. "
      + "Il ne relit pas le CV : ce qu'il en fallait est déjà dans les expériences."),
    h`<span class="sm sourd pousse">comptez une dizaine de secondes</span>`));
  const arreter = compteur('#chrono');

  try {
    const r = await api.post<{ traitement: string; cout_eur: number; synthese: Synthese;
      lu: { experiences: number; notes: number; documents: number } }>(
      `/api/profils/${vivante!.profilId}/synthese`);
    vivante!.traitement = r.traitement;
    vivante!.produite = r.synthese;
    arreter();
    relecture(r.synthese, r.cout_eur, r.lu);
  } catch (e) {
    arreter();
    peindre('#couche', cadre('Synthèse impossible', h`
      <p class="err" style="display:block">${direRefus(e)}</p>`,
      h`<button class="bt pousse" data-act="fermer">Fermer</button>`));
  }
}

/**
 * Ce que le modèle propose de positionnement, ligne à ligne, avec son appui et une case à
 * décocher. Décocher est l'opération qui compte : le défaut d'un modèle est d'en mettre
 * trop, et une ligne sans appui vrai pollue le vivier pour longtemps. Ce qui reste coché
 * s'enregistre — règle 8, le modèle propose, l'humain tranche.
 */
const visees = (titre: string, cle: 'secteur' | 'direction', lignes: Vise[] | undefined): Brut => {
  if (!lignes?.length) return h`<div><label class="lab">${titre}</label>
    <p class="sm sourd" style="margin:0">Le dossier n'en appuie aucun. C'est une information.</p></div>`;
  return h`<div><label class="lab">${titre} · DÉCOCHEZ CE QUE LE DOSSIER N'APPUIE PAS</label>
    ${lignes.map((v, i) => h`<label class="rang" style="gap:8px;margin-top:5px;align-items:flex-start;cursor:pointer">
      <input type="checkbox" data-v="${cle}" data-i="${i}" checked style="margin-top:3px">
      <span class="pousse"><span class="sm" style="color:var(--obsidian)">${v[cle] ?? ''}</span>
        <span class="pt sourd" style="display:block;line-height:1.5">${v.appui}</span></span></label>`)}
  </div>`;
};

/**
 * La cartographie telle qu'elle va s'écrire. En lecture seule ici : les crans se corrigent
 * sur la fiche, curseur et cases à l'appui, par quelqu'un qui a passé une heure avec la
 * personne. L'afficher quand même est ce qui rend la relecture honnête — on n'enregistre
 * pas en aveugle des chiffres qu'on n'a pas vus.
 */
const cartographie = (s: Synthese): Brut => {
  const lus = (liste: readonly { cle: string; libelle: string }[], axe: Axe | undefined) =>
    liste.map((x) => ({ ...x, n: niveauAxe(axe, x.cle) })).filter((x) => x.n)
      .sort((a, b) => b.n - a.n);
  const chaine = lus(ETAPES_CHAINE, s.ia_chaine);
  const terrains = lus(TERRAINS_IA, s.ia_terrains);
  const rien = !chaine.length && !terrains.length && !s.ia_postures?.length && !s.ia_niveau;
  const ligne = (x: { libelle: string; n: number }) =>
    h`<div class="rang" style="gap:9px;margin-top:4px"><span class="sm pousse">${x.libelle}</span>
      <span class="pt" style="color:var(--copper)">${NIVEAUX_AXE[x.n]}</span></div>`;

  return h`<div><label class="lab">CARTOGRAPHIE IA · CORRIGIBLE SUR LA FICHE APRÈS ENREGISTREMENT</label>
    ${rien ? h`<p class="sm sourd" style="margin:0">Le dossier ne permet de rien constater sur l'IA.
        Ce qui est déjà saisi à la main sur la fiche sera conservé.</p>` : h`
      <div class="rang" style="gap:12px;flex-wrap:wrap">
        <span class="sm">${s.ia_niveau ? h`Profondeur <b>${s.ia_niveau} / 5</b> · ${NIVEAUX_IA[s.ia_niveau]}` : 'Profondeur non tranchée'}</span>
        ${s.ia_depuis ? h`<span class="pt sourd">depuis ${s.ia_depuis}</span>` : ''}
        ${s.ia_dernier ? h`<span class="pt sourd">dernier projet ${s.ia_dernier}</span>` : ''}</div>
      ${s.ia_postures?.length ? h`<div class="rang" style="gap:5px;flex-wrap:wrap;margin-top:7px">
        ${s.ia_postures.map((x) => h`<span class="et o">${x}</span>`)}</div>` : ''}
      ${chaine.length ? h`<div style="margin-top:9px">
        <div class="pt sourd" style="letter-spacing:1.2px">LA CHAÎNE</div>${chaine.map(ligne)}</div>` : ''}
      ${terrains.length ? h`<div style="margin-top:9px">
        <div class="pt sourd" style="letter-spacing:1.2px">LES TERRAINS</div>${terrains.map(ligne)}</div>` : ''}
      ${s.ia_justification ? h`<p class="pt sourd" style="margin:9px 0 0;line-height:1.55">${s.ia_justification}</p>` : ''}`}
  </div>`;
};

function relecture(s: Synthese, cout: number, lu: { experiences: number; notes: number; documents: number }): void {
  peindre('#couche', cadre('Relire avant d’enregistrer', h`
    <div class="enc gl"><span class="sur">CE QUI A ÉTÉ LU</span>
      <p>${lu.experiences} expérience${lu.experiences > 1 ? 's' : ''} ·
         ${lu.notes} note${lu.notes > 1 ? 's' : ''} ·
         ${lu.documents} pièce${lu.documents > 1 ? 's' : ''} d'entretien ou de référence.
         ${lu.documents === 0 ? "Aucun compte rendu d'entretien au dossier : la synthèse ne repose que sur du déclaratif." : ''}</p></div>

    <div><label class="lab" for="s-positionnement">POSITIONNEMENT</label>
      <textarea class="champ" id="s-positionnement" rows="4">${s.positionnement}</textarea></div>

    <div><label class="lab">SAVOIR-FAIRE PROUVÉ · CHAQUE LIGNE PORTE SA SOURCE</label>
      ${s.prouve.map(fait)}
      ${s.prouve.length === 0 ? h`<p class="sm sourd">Rien n'a paru établi. C'est une information.</p>` : ''}</div>

    <div><label class="lab" for="s-vigilance">POINTS DE VIGILANCE · UN PAR LIGNE</label>
      <textarea class="champ" id="s-vigilance" rows="3">${s.vigilance.join('\n')}</textarea></div>

    <div><label class="lab" for="s-ouvertes">QUESTIONS OUVERTES · UNE PAR LIGNE</label>
      <textarea class="champ" id="s-ouvertes" rows="3">${s.ouvertes.join('\n')}</textarea></div>

    ${visees("POSITIONNEMENT · SECTEURS D'ACTIVITÉ", 'secteur', s.secteurs_cibles)}
    ${visees('POSITIONNEMENT · DIRECTIONS MÉTIERS', 'direction', s.directions_cibles)}
    ${cartographie(s)}

    <p class="err" id="err" hidden></p>`,
    h`<span class="sm sourd pousse">${cout.toFixed(3).replace('.', ',')} €</span>
      <button class="bt" data-act="fermer">Abandonner</button>
      <button class="bt p" data-s-act="enregistrer">Enregistrer la synthèse</button>`));

  vivante!.empreinte = JSON.stringify(recolter());
}

/** Ce que le formulaire de relecture sait écrire. Tout le reste de la synthèse n'y est
 *  pas représenté, et doit donc repartir tel que le modèle l'a rendu. */
export interface Saisi {
  positionnement: string; prouve: Fait[]; vigilance: string[]; ouvertes: string[];
  secteurs_cibles: Vise[]; directions_cibles: Vise[];
}

/**
 * La fusion, isolée pour être éprouvée sans navigateur : c'est exactement ici que vivait
 * le défaut. Le formulaire reconstruisait un objet neuf à partir de ses seuls champs, et
 * la cartographie IA comme le positionnement sectoriel disparaissaient en silence entre
 * la production et l'enregistrement — le serveur recevait des axes vides, qu'il traite
 * à juste titre comme « rien à écrire ».
 */
export const fusionner = (produite: Synthese, saisi: Saisi): Synthese => ({ ...produite, ...saisi });

function recolter(): Synthese {
  const lignes = (cle: string) =>
    (($(`#s-${cle}`) as HTMLTextAreaElement | null)?.value ?? '').split('\n').map((x) => x.trim()).filter(Boolean);
  const nombre = document.querySelectorAll('[data-s="fait"]').length;
  const dans = (i: number, nom: string) =>
    ($(`[data-s="${nom}"][data-i="${i}"]`) as HTMLInputElement | HTMLTextAreaElement | null)?.value.trim() ?? '';

  // Les cases décochées sortent ; tout ce que le formulaire ne montre pas repart entier.
  const garde = (cle: 'secteur' | 'direction', liste: Vise[] | undefined) => (liste ?? [])
    .filter((_, i) => ($(`[data-v="${cle}"][data-i="${i}"]`) as HTMLInputElement | null)?.checked ?? true);

  const produite = vivante?.produite ?? VIDE;
  return fusionner(produite, {
    positionnement: valeur('#s-positionnement'),
    prouve: Array.from({ length: nombre }, (_, i) => ({ fait: dans(i, 'fait'), source: dans(i, 'source') }))
      .filter((f) => f.fait && f.source),
    vigilance: lignes('vigilance'),
    ouvertes: lignes('ouvertes'),
    secteurs_cibles: garde('secteur', produite.secteurs_cibles),
    directions_cibles: garde('direction', produite.directions_cibles),
  });
}

function poserEcoute(): void {
  if (ecoute) return;
  ecoute = true;

  $('#couche')?.addEventListener('click', async (ev) => {
    const cible = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-s-act]');
    if (!cible || !vivante) return;

    const dire = (message: string) => {
      const n = $('#err');
      if (n) { n.textContent = message; n.hidden = false; }
    };

    const synthese = recolter();
    if (!synthese.positionnement) return dire('Le positionnement est obligatoire.');
    const suite = JSON.stringify(synthese) === vivante.empreinte ? 'accepte' : 'modifie';

    try {
      await api.remplacer(`/api/profils/${vivante.profilId}/synthese`,
        { synthese, traitement: vivante.traitement, suite });
      const fini = vivante.fini;
      vivante = null;
      mot('Synthèse enregistrée.');
      fini();
    } catch (e) { dire(direRefus(e)); }
  });
}
